import React, { useEffect, useState } from 'react';
import { Issue, Sprint } from '../../data/mockData';
import { useProjectContext } from '../../hooks/useProjectContext';
import { useBrandConfig } from '../../hooks/useBrandConfig';
import { runTool } from '../../services/toolRunner';
import { IssueList } from '../shared/IssueList';
import { SprintBoardView } from './SprintBoardView';
import { RefreshButton } from '../shared/RefreshButton';
import { useGithubData } from '../../hooks/useGithubData';
import { Calendar, Target, ChevronDown, ChevronRight, AlertCircle, Loader2, List, CheckCircle2, ExternalLink } from 'lucide-react';
/**
 * Enrich incomplete issues (ID-only from search_issues) by fetching full details.
 * Fetches up to 15 issues in batches of 5.
 */
async function enrichIncompleteIssues(issues: Issue[]): Promise<Issue[]> {
  const incomplete = issues.filter(i => !i.summary || i.summary.startsWith('#') || i.key.startsWith('#'));
  if (incomplete.length === 0) return issues;
  console.log(`[SprintListView] Enriching ${incomplete.length} incomplete issues...`);
  const toEnrich = incomplete.slice(0, 15);
  const enriched = new Map<string, Issue>();
  const batchSize = 5;
  for (let i = 0; i < toEnrich.length; i += batchSize) {
    const batch = toEnrich.slice(i, i + batchSize);
    const results = await Promise.all(batch.map(async issue => {
      const issueId = issue.key.replace('#', '');
      try {
        const result = await runTool('get-issue', {
          issue_key: issueId
        });
        if (result.success && result.data) {
          const mapped = mapJiraIssue(result.data);
          if (mapped && !mapped.summary.startsWith('#')) {
            return {
              id: issue.key,
              data: mapped
            };
          }
        }
      } catch (e) {
        console.warn(`[SprintListView] Failed to enrich ${issueId}:`, e);
      }
      return null;
    }));
    results.forEach(r => {
      if (r) enriched.set(r.id, r.data);
    });
  }
  if (enriched.size === 0) return issues;
  console.log(`[SprintListView] Enriched ${enriched.size} issues`);
  return issues.map(issue => enriched.get(issue.key) || issue);
}
/**
 * Map a raw Jira issue (from MCP) to our Issue type.
 * Handles incomplete issues (e.g. search_issues only returns {id} without key/summary).
 * Returns null for issues that have no usable data (filter them out).
 */
function mapJiraIssue(ji: any): Issue | null {
  const fields = ji.fields || {};
  const key = ji.key || (ji.id ? `#${ji.id}` : '');
  const summary = fields.summary || ji.summary || '';
  // Skip issues with no key AND no summary (completely empty from search_issues)
  if (!key && !summary) return null;
  return {
    key,
    summary: summary || key || 'Issue sin datos',
    type: fields.issuetype?.name?.toLowerCase().includes('bug') ? 'Bug' as const : fields.issuetype?.name?.toLowerCase().includes('epic') ? 'Epic' as const : fields.issuetype?.name?.toLowerCase().includes('story') ? 'Story' as const : 'Task' as const,
    status: fields.status?.name?.toLowerCase().includes('done') ? 'Done' as const : fields.status?.name?.toLowerCase().includes('progress') ? 'In Progress' as const : fields.status?.name?.toLowerCase().includes('review') ? 'Review' as const : 'To Do' as const,
    rawStatus: fields.status?.name || ji.status || undefined,
    priority: fields.priority?.name?.toLowerCase().includes('high') ? 'High' as const : fields.priority?.name?.toLowerCase().includes('low') ? 'Low' as const : 'Medium' as const,
    points: fields.customfield_10016 || fields.story_points || ji.storyPoints || undefined,
    assignee: fields.assignee ? {
      id: fields.assignee.accountId || 'unknown',
      name: fields.assignee.displayName || 'Sin asignar',
      role: '',
      avatar: fields.assignee.avatarUrls?.['24x24'] || '',
      initials: (fields.assignee.displayName || 'SA').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2),
      workload: 0
    } : undefined,
    labels: fields.labels || ji.labels || []
  };
}
interface SprintListViewProps {
  projectIssues: Issue[];
  projectBacklog: Issue[];
  activeSprint: Sprint;
  isLive: boolean;
  onRefresh: () => Promise<void>;
}
export function SprintListView({
  projectIssues,
  projectBacklog,
  activeSprint,
  isLive,
  onRefresh
}: SprintListViewProps) {
  const {
    brand
  } = useBrandConfig();
  const {
    activeProject
  } = useProjectContext();
  const {
    prs: githubPrs,
    commits: githubCommits,
    branches: githubBranches
  } = useGithubData(activeProject?.key);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [boardId, setBoardId] = useState<number | null>(null);
  const [expandedSprints, setExpandedSprints] = useState<Record<string, boolean>>({});
  const [sprintIssuesMap, setSprintIssuesMap] = useState<Record<string, Issue[]>>({});
  const [loadingIssues, setLoadingIssues] = useState<Record<string, boolean>>({});
  // Sort sprints chronologically: oldest first (Sprint 1 → Sprint 2 → Sprint 3...)
  const sortSprints = (list: Sprint[]): Sprint[] => {
    return [...list].sort((a, b) => {
      // Primary: by startDate ascending (oldest first = 1, 2, 3...)
      const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
      const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
      if (dateA !== dateB) return dateA - dateB;
      // Fallback: Completed < Active < Planned
      const order = {
        Completed: 0,
        Active: 1,
        Planned: 2
      };
      return (order[a.status] ?? 3) - (order[b.status] ?? 3);
    });
  };
  const fetchAllSprints = async () => {
    if (!activeProject) return;
    setLoading(true);
    setError(null);
    try {
      if (isLive) {
        // 1. Get boards for project
        const boardsRes = await runTool('get-boards', {
          projectKey: activeProject.key
        });
        if (!boardsRes.success) throw new Error(boardsRes.error || 'Error al obtener boards');
        const allBoards = Array.isArray(boardsRes.data?.values) ? boardsRes.data.values : Array.isArray(boardsRes.data) ? boardsRes.data : [];
        // Filter for Scrum boards only (Kanban boards don't support sprints)
        const scrumBoards = allBoards.filter((b: any) => b.type === 'scrum' || b.type === 'simple');
        // If no scrum board, try agile_get_active_sprints as fallback
        if (scrumBoards.length === 0) {
          console.log('[SprintListView] No scrum boards found, trying agile_get_active_sprints fallback');
          const activeRes = await runTool('agile-get-active-sprints', {});
          if (activeRes.success) {
            const activeValues = Array.isArray(activeRes.data?.values) ? activeRes.data.values : Array.isArray(activeRes.data) ? activeRes.data : [];
            // Filter by project's boards
            const projectBoardIds = new Set(allBoards.map((b: any) => b.id));
            const projectSprints = activeValues.filter((s: any) => projectBoardIds.has(s.originBoardId) || !s.originBoardId);
            if (projectSprints.length > 0) {
              const firstOriginBoard = projectSprints[0].originBoardId;
              if (firstOriginBoard) setBoardId(firstOriginBoard);
              const mappedSprints: Sprint[] = projectSprints.map((s: any) => ({
                id: String(s.id),
                name: s.name || `Sprint ${s.id}`,
                goal: s.goal || '',
                startDate: s.startDate ? s.startDate.split('T')[0] : '',
                endDate: s.endDate ? s.endDate.split('T')[0] : '',
                status: 'Active' as const,
                progress: 0,
                totalPoints: 0,
                completedPoints: 0
              }));
              setSprints(mappedSprints);
              if (mappedSprints.length > 0) {
                setExpandedSprints({
                  [mappedSprints[0].id]: true
                });
                setSprintIssuesMap({
                  [mappedSprints[0].id]: projectIssues
                });
              }
              return;
            }
          }
          setSprints([]);
          return;
        }
        // 2. Get all sprints for the first Scrum board
        const firstBoardId = scrumBoards[0].id;
        setBoardId(firstBoardId);
        const sprintsRes = await runTool('get-sprints', {
          board_id: String(firstBoardId)
        });
        if (!sprintsRes.success) throw new Error(sprintsRes.error || 'Error al obtener sprints');
        const rawSprints = Array.isArray(sprintsRes.data?.values) ? sprintsRes.data.values : Array.isArray(sprintsRes.data) ? sprintsRes.data : [];
        const mappedSprints: Sprint[] = rawSprints.map((s: any) => ({
          id: String(s.id),
          name: s.name || `Sprint ${s.id}`,
          goal: s.goal || '',
          startDate: s.startDate ? s.startDate.split('T')[0] : '',
          endDate: s.endDate ? s.endDate.split('T')[0] : '',
          status: s.state === 'active' ? 'Active' as const : s.state === 'closed' ? 'Completed' as const : 'Planned' as const,
          progress: 0,
          totalPoints: 0,
          completedPoints: 0
        }));
        const sorted = sortSprints(mappedSprints);
        setSprints(sorted);
        // Auto-expand active sprint and pre-load its issues
        const active = sorted.find(s => s.status === 'Active');
        if (active) {
          setExpandedSprints({
            [active.id]: true
          });
          setSprintIssuesMap({
            [active.id]: projectIssues
          });
        }
        // Pre-fetch issues for ALL sprints in background
        const otherSprints = sorted.filter(s => s.id !== active?.id);
        if (otherSprints.length > 0) {
          prefetchSprintIssues(otherSprints);
        }
      } else {
        // Mock mode: Use sprints from activeProject if available, otherwise fallback to activeSprint
        await new Promise(r => setTimeout(r, 200));
        const mockSprints = activeProject?.sprints && activeProject.sprints.length > 0 ? sortSprints(activeProject.sprints) : [activeSprint];
        setSprints(mockSprints);
        // Auto-expand active sprint and set its issues
        const active = mockSprints.find(s => s.status === 'Active');
        if (active) {
          setExpandedSprints({
            [active.id]: true
          });
          setSprintIssuesMap({
            [active.id]: projectIssues
          });
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAllSprints();
  }, [activeProject?.key, isLive]);
  // Keep active sprint issues in sync when projectIssues change
  useEffect(() => {
    const active = sprints.find(s => s.status === 'Active');
    if (active && projectIssues.length > 0) {
      setSprintIssuesMap(prev => ({
        ...prev,
        [active.id]: projectIssues
      }));
    }
  }, [projectIssues, sprints]);
  // Pre-fetch issues for multiple sprints in background
  const prefetchSprintIssues = async (sprintList: Sprint[]) => {
    for (const sprint of sprintList) {
      try {
        // Try get-sprint-issues first (requires board_id + sprint_id)
        let issues: any[] = [];
        if (boardId) {
          const res = await runTool('get-sprint-issues', {
            board_id: String(boardId),
            sprint_id: sprint.id
          });
          if (res.success) {
            issues = Array.isArray(res.data?.issues) ? res.data.issues : Array.isArray(res.data) ? res.data : [];
          }
        }
        // Fallback: JQL search
        if (issues.length === 0) {
          const jqlRes = await runTool('search-issues', {
            jql: `sprint = ${sprint.id} ORDER BY priority DESC, created DESC`,
            max_results: 50
          });
          if (jqlRes.success) {
            const jqlIssues = Array.isArray(jqlRes.data?.issues) ? jqlRes.data.issues : Array.isArray(jqlRes.data) ? jqlRes.data : [];
            if (jqlIssues.length > 0) issues = jqlIssues;
          }
        }
        let mappedIssues = issues.map(mapJiraIssue).filter((i): i is Issue => i !== null);
        // Enrich ID-only issues (from search_issues fallback)
        if (mappedIssues.some(i => i.key.startsWith('#') || !i.summary || i.summary.startsWith('#'))) {
          mappedIssues = await enrichIncompleteIssues(mappedIssues);
        }
        setSprintIssuesMap(prev => ({
          ...prev,
          [sprint.id]: mappedIssues
        }));
      } catch (err) {
        console.error(`[SprintListView] prefetch failed for sprint ${sprint.id}:`, err);
      }
    }
  };
  const toggleSprint = async (sprintId: string) => {
    const isExpanding = !expandedSprints[sprintId];
    setExpandedSprints(prev => ({
      ...prev,
      [sprintId]: isExpanding
    }));
    // If expanding and we don't have issues yet, fetch them
    if (isExpanding && !sprintIssuesMap[sprintId]) {
      const sprint = sprints.find(s => s.id === sprintId);
      // Active sprint already has issues from context
      if (sprint?.status === 'Active') {
        setSprintIssuesMap(prev => ({
          ...prev,
          [sprintId]: projectIssues
        }));
        return;
      }
      if (isLive) {
        setLoadingIssues(prev => ({
          ...prev,
          [sprintId]: true
        }));
        try {
          // Try get-sprint-issues first (requires board_id + sprint_id)
          let issues: any[] = [];
          if (boardId) {
            const res = await runTool('get-sprint-issues', {
              board_id: String(boardId),
              sprint_id: sprintId
            });
            if (res.success) {
              issues = Array.isArray(res.data?.issues) ? res.data.issues : Array.isArray(res.data) ? res.data : [];
            }
          }
          // Fallback: if no issues found, try JQL search with sprint = id
          if (issues.length === 0) {
            console.log(`[SprintListView] get-sprint-issues returned 0, trying JQL fallback for sprint ${sprintId}`);
            const jqlRes = await runTool('search-issues', {
              jql: `sprint = ${sprintId} ORDER BY priority DESC, created DESC`,
              max_results: 50
            });
            if (jqlRes.success) {
              const jqlIssues = Array.isArray(jqlRes.data?.issues) ? jqlRes.data.issues : Array.isArray(jqlRes.data) ? jqlRes.data : [];
              if (jqlIssues.length > 0) {
                issues = jqlIssues;
              }
            }
          }
          let mappedIssues = issues.map(mapJiraIssue).filter((i): i is Issue => i !== null);
          // Enrich ID-only issues (from search_issues fallback)
          if (mappedIssues.some(i => i.key.startsWith('#') || !i.summary || i.summary.startsWith('#'))) {
            mappedIssues = await enrichIncompleteIssues(mappedIssues);
          }
          setSprintIssuesMap(prev => ({
            ...prev,
            [sprintId]: mappedIssues
          }));
        } catch (err) {
          console.error('Error fetching sprint issues:', err);
        } finally {
          setLoadingIssues(prev => ({
            ...prev,
            [sprintId]: false
          }));
        }
      } else {
        // Mock mode: show some backlog issues for non-active sprints
        setLoadingIssues(prev => ({
          ...prev,
          [sprintId]: true
        }));
        await new Promise(r => setTimeout(r, 300));
        // Give completed sprints some "done" issues, planned sprints some backlog
        if (sprint?.status === 'Completed') {
          const doneIssues = projectIssues.filter(i => i.status === 'Done').concat(projectBacklog.slice(0, 2)).map(i => ({
            ...i,
            status: 'Done' as const
          }));
          setSprintIssuesMap(prev => ({
            ...prev,
            [sprintId]: doneIssues.length > 0 ? doneIssues : projectBacklog.slice(0, 4).map(i => ({
              ...i,
              status: 'Done' as const
            }))
          }));
        } else {
          setSprintIssuesMap(prev => ({
            ...prev,
            [sprintId]: projectBacklog.slice(0, 4)
          }));
        }
        setLoadingIssues(prev => ({
          ...prev,
          [sprintId]: false
        }));
      }
    }
  };
  const handleRefresh = async () => {
    await onRefresh();
    fetchAllSprints();
  };
  if (loading) {
    return <div className="flex-1 overflow-y-auto p-8 flex items-center justify-center" data-id="element-6879">
        <div className="flex flex-col items-center text-slate-500" data-id="element-6880">
          <Loader2 size={32} className="animate-spin mb-4 text-indigo-500" data-id="element-6881" />
          <p data-id="element-6882">Cargando sprints...</p>
        </div>
      </div>;
  }
  if (error) {
    return <div className="flex-1 overflow-y-auto p-8" data-id="element-6883">
        <div className="max-w-5xl mx-auto bg-red-950/20 border border-red-900/50 rounded-xl p-6 text-center" data-id="element-6884">
          <AlertCircle size={32} className="mx-auto text-red-400 mb-3" data-id="element-6885" />
          <h3 className="text-lg font-bold text-red-400 mb-1" data-id="element-6886">
            Error al cargar sprints
          </h3>
          <p className="text-sm text-red-300/80" data-id="element-6887">{error}</p>
          <button onClick={handleRefresh} className="mt-4 px-4 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-300 rounded-lg text-sm transition-colors" data-id="element-6888">
            Reintentar
          </button>
        </div>
      </div>;
  }
  const hasSprints = sprints.length > 0;
  // Count stats
  const activeSprints = sprints.filter(s => s.status === 'Active').length;
  const plannedSprints = sprints.filter(s => s.status === 'Planned').length;
  const completedSprints = sprints.filter(s => s.status === 'Completed').length;
  // Build Jira URLs
  const jiraSiteUrl = brand.jiraBaseUrl.replace(/\/browse\/?$/, '');
  const getSprintUrl = (sprintId: string) => {
    if (boardId && activeProject?.key) {
      return `${jiraSiteUrl}/jira/software/projects/${activeProject.key}/boards/${boardId}?sprint=${sprintId}`;
    }
    return null;
  };
  const getBoardUrl = () => {
    if (boardId && activeProject?.key) {
      return `${jiraSiteUrl}/jira/software/projects/${activeProject.key}/boards/${boardId}`;
    }
    return null;
  };
  return <div className="flex-1 overflow-y-auto p-8 animate-in fade-in duration-300" data-id="element-6889">
      <div className="max-w-5xl mx-auto" data-id="element-6890">
        <div className="flex items-center justify-between mb-6" data-id="element-6891">
          <div data-id="element-6892">
            <h2 className="text-2xl font-bold text-slate-100" data-id="element-6893">Sprints</h2>
            <p className="text-slate-500 text-sm mt-1" data-id="element-6894">
              {hasSprints ? `${sprints.length} sprints · ${activeSprints} activo · ${plannedSprints} planificado · ${completedSprints} completado` : 'No hay sprints creados'}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-6895">
            {getBoardUrl() && <a href={getBoardUrl()!} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-400 bg-indigo-950/30 border border-indigo-900/40 rounded-lg hover:bg-indigo-950/50 transition-colors" data-id="element-6896">
                <ExternalLink size={12} data-id="element-6897" />
                Ver Board en Jira
              </a>}
            <RefreshButton onRefresh={handleRefresh} label="Refresh" data-id="element-6898" />
          </div>
        </div>

        {hasSprints ? <div className="space-y-3" data-id="element-6899">
            {sprints.map(sprint => {
          const isExpanded = expandedSprints[sprint.id];
          const issues = sprintIssuesMap[sprint.id] || [];
          const isLoadingSprintIssues = loadingIssues[sprint.id];
          const isActive = sprint.status === 'Active';
          const isCompleted = sprint.status === 'Completed';
          const isPlanned = sprint.status === 'Planned';
          const statusColor = isActive ? 'bg-emerald-950/50 text-emerald-400 border-emerald-900/50' : isPlanned ? 'bg-blue-950/50 text-blue-400 border-blue-900/50' : 'bg-slate-800 text-slate-400 border-slate-700';
          const statusLabel = isActive ? 'ACTIVO' : isPlanned ? 'PLANIFICADO' : 'COMPLETADO';
          const cardBorder = isExpanded ? 'border-indigo-500/50 shadow-lg shadow-indigo-500/5' : isActive ? 'border-emerald-800/50 hover:border-emerald-700/50' : 'border-slate-800 hover:border-slate-700';
          // Calculate days remaining for active sprint
          let daysRemaining: number | null = null;
          if (isActive && sprint.endDate) {
            const end = new Date(sprint.endDate);
            const now = new Date();
            daysRemaining = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
          }
          return <div key={sprint.id} className={`bg-slate-900 rounded-xl border transition-all duration-200 ${cardBorder}`} data-id="element-6900">
                  {/* Sprint Header */}
                  <button onClick={() => toggleSprint(sprint.id)} className="w-full text-left p-5 flex items-center justify-between group" data-id="element-6901">
                    <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-6902">
                      <div className={`p-2 rounded-lg transition-colors shrink-0 ${isExpanded ? 'bg-indigo-500/10 text-indigo-400' : 'bg-slate-800 text-slate-500 group-hover:text-slate-300'}`} data-id="element-6903">
                        {isExpanded ? <ChevronDown size={20} data-id="element-6904" /> : <ChevronRight size={20} data-id="element-6905" />}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-6906">
                        <div className="flex items-center gap-3 mb-1 flex-wrap" data-id="element-6907">
                          <h3 className={`text-base font-bold ${isCompleted ? 'text-slate-400' : 'text-slate-100'}`} data-id="element-6908">
                            {sprint.name}
                          </h3>
                          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${statusColor}`} data-id="element-6909">
                            {statusLabel}
                          </span>
                          {isActive && daysRemaining !== null && <span className="text-[10px] text-amber-400 bg-amber-950/30 border border-amber-900/30 px-2 py-0.5 rounded-full shrink-0" data-id="element-6910">
                              {daysRemaining}d restantes
                            </span>}
                          {isCompleted && <CheckCircle2 size={14} className="text-slate-600 shrink-0" data-id="element-6911" />}
                          {/* Jira link */}
                          {getSprintUrl(sprint.id) && <a href={getSprintUrl(sprint.id)!} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-slate-700 text-slate-500 hover:text-indigo-400 shrink-0" title="Ver sprint en Jira" data-id="element-6912">
                              <ExternalLink size={14} data-id="element-6913" />
                            </a>}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500" data-id="element-6914">
                          {(sprint.startDate || sprint.endDate) && <span className="flex items-center gap-1.5" data-id="element-6915">
                              <Calendar size={12} data-id="element-6916" />
                              {sprint.startDate || '?'} →{' '}
                              {sprint.endDate || '?'}
                            </span>}
                          {sprint.goal && <span className="flex items-center gap-1.5 truncate max-w-sm" data-id="element-6917">
                              <Target size={12} className="shrink-0" data-id="element-6918" />
                              {sprint.goal}
                            </span>}
                          {sprint.totalPoints > 0 && <span className="shrink-0" data-id="element-6919">
                              {sprint.completedPoints}/{sprint.totalPoints} pts
                            </span>}
                        </div>
                      </div>
                    </div>

                    {/* Progress bar */}
                    {(isActive || isCompleted) && sprint.progress !== undefined && <div className="hidden sm:flex items-center gap-3 w-44 shrink-0 ml-4" data-id="element-6920">
                          <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden" data-id="element-6921">
                            <div className={`h-full rounded-full transition-all ${isCompleted ? 'bg-slate-600' : 'bg-indigo-500'}`} style={{
                    width: `${sprint.progress}%`
                  }} data-id="element-6922" />
                          </div>
                          <span className="text-xs font-mono text-slate-400 w-8 text-right" data-id="element-6923">
                            {sprint.progress}%
                          </span>
                        </div>}
                  </button>

                  {/* Expanded: Issues */}
                  {isExpanded && <div className="px-5 pb-5 border-t border-slate-800/50" data-id="element-6924">
                      <div className="pt-4" data-id="element-6925">
                        {/* Sprint detail summary */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-id="element-6926">
                          <div className="bg-slate-950/60 rounded-lg p-3 border border-slate-800/50" data-id="element-6927">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-id="element-6928">
                              Estado
                            </p>
                            <p className={`text-sm font-semibold ${isActive ? 'text-emerald-400' : isCompleted ? 'text-slate-400' : 'text-blue-400'}`} data-id="element-6929">
                              {statusLabel}
                            </p>
                          </div>
                          <div className="bg-slate-950/60 rounded-lg p-3 border border-slate-800/50" data-id="element-6930">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-id="element-6931">
                              Duración
                            </p>
                            <p className="text-sm font-semibold text-slate-300" data-id="element-6932">
                              {sprint.startDate && sprint.endDate ? `${Math.ceil((new Date(sprint.endDate).getTime() - new Date(sprint.startDate).getTime()) / (1000 * 60 * 60 * 24))} días` : 'Sin definir'}
                            </p>
                          </div>
                          <div className="bg-slate-950/60 rounded-lg p-3 border border-slate-800/50" data-id="element-6933">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-id="element-6934">
                              Story Points
                            </p>
                            <p className="text-sm font-semibold text-slate-300" data-id="element-6935">
                              {sprint.totalPoints > 0 ? `${sprint.completedPoints}/${sprint.totalPoints}` : issues.length > 0 ? `${issues.reduce((s, i) => s + (i.points || 0), 0)} pts` : '—'}
                            </p>
                          </div>
                          <div className="bg-slate-950/60 rounded-lg p-3 border border-slate-800/50" data-id="element-6936">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-id="element-6937">
                              Issues
                            </p>
                            <p className="text-sm font-semibold text-slate-300" data-id="element-6938">
                              {isLoadingSprintIssues ? '...' : issues.length > 0 ? issues.length : '0'}
                            </p>
                          </div>
                        </div>

                        {/* Goal if present */}
                        {sprint.goal && <div className="mb-4 p-3 bg-indigo-950/20 border border-indigo-900/30 rounded-lg" data-id="element-6939">
                            <p className="text-[10px] uppercase tracking-wider text-indigo-400/70 mb-1 flex items-center gap-1.5" data-id="element-6940">
                              <Target size={10} data-id="element-6941" /> Objetivo del Sprint
                            </p>
                            <p className="text-sm text-slate-300" data-id="element-6942">
                              {sprint.goal}
                            </p>
                          </div>}

                        {/* Issues list or empty state */}
                        {isLoadingSprintIssues ? <div className="py-8 flex justify-center" data-id="element-6943">
                            <Loader2 size={24} className="animate-spin text-slate-600" data-id="element-6944" />
                          </div> : issues.length > 0 ? <>
                            <div className="flex items-center justify-between mb-3" data-id="element-6945">
                              <h4 className="text-sm font-semibold text-slate-300" data-id="element-6946">
                                Issues del Sprint
                              </h4>
                              <span className="text-xs text-slate-500 bg-slate-800 px-2 py-1 rounded-md" data-id="element-6947">
                                {issues.length} issues ·{' '}
                                {issues.reduce((s, i) => s + (i.points || 0), 0)}{' '}
                                pts
                              </span>
                            </div>
                            <SprintBoardView issues={issues} jiraBaseUrl={brand.jiraBaseUrl} githubPrs={githubPrs} githubCommits={githubCommits} githubBranches={githubBranches} boardId={isLive ? boardId : null} data-id="element-6948" />
                          </> : <div className="py-4 text-center text-slate-500 text-sm" data-id="element-6949">
                            {isPlanned ? 'Este sprint aún no tiene issues asignados. Podés mover issues desde el backlog cuando esté listo.' : isCompleted ? 'No se encontraron issues para este sprint completado.' : 'No hay issues en este sprint.'}
                          </div>}
                      </div>
                    </div>}
                </div>;
        })}
          </div> : <div className="py-16 text-center bg-slate-900 rounded-xl border border-slate-800" data-id="element-6950">
            <Calendar size={40} className="mx-auto text-slate-700 mb-4" data-id="element-6951" />
            <h3 className="text-lg font-bold text-slate-300 mb-2" data-id="element-6952">
              No hay sprints
            </h3>
            <p className="text-sm text-slate-500" data-id="element-6953">
              Este proyecto no tiene sprints creados. El backlog se muestra
              debajo.
            </p>
          </div>}

        {/* Backlog Section */}
        {projectBacklog.length > 0 && <div className="mt-8" data-id="element-6954">
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6" data-id="element-6955">
              <div className="flex items-center justify-between mb-6" data-id="element-6956">
                <div data-id="element-6957">
                  <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2" data-id="element-6958">
                    <List className="text-indigo-400" data-id="element-6959" />
                    Backlog
                  </h3>
                  <p className="text-sm text-slate-500 mt-1" data-id="element-6960">
                    Issues pendientes de planificar
                  </p>
                </div>
                <span className="text-xs font-medium bg-slate-800 text-slate-400 px-3 py-1 rounded-full" data-id="element-6961">
                  {projectBacklog.length} issues
                </span>
              </div>
              <IssueList issues={projectBacklog} jiraBaseUrl={brand.jiraBaseUrl} githubPrs={githubPrs} githubCommits={githubCommits} githubBranches={githubBranches} data-id="element-6962" />
            </div>
          </div>}
      </div>
    </div>;
}